'use client';

import React from 'react';
import { Calendar, Info, Repeat } from 'lucide-react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { getLocalDateString } from '@crm-src/lib/date-utils';
import { cadenceSummary, cadenceUnitLabel, type RecurringFrequency } from '@crm-src/lib/cadence';
import { CadencePresetChips } from '@crm-src/components/cadence-preset-chips';
import { WholeNumberField } from '@crm-src/components/whole-number-field';

interface SchedulingCardProps {
  documentType: 'invoice' | 'quote';
  selectedContact: any | null;

  enableRecurring: boolean;
  setEnableRecurring: (value: boolean) => void;

  recurringFrequency: RecurringFrequency;
  setRecurringFrequency: (value: RecurringFrequency) => void;
  recurringInterval: number;
  setRecurringInterval: (value: number) => void;
  recurringStartDate: string;
  setRecurringStartDate: (value: string) => void;
  recurringDayOfWeek: number;
  setRecurringDayOfWeek: (value: number) => void;
  recurringDayOfMonth: number;
  setRecurringDayOfMonth: (value: number) => void;
  recurringGenerateDaysBeforePeriodEnd: number;
  setRecurringGenerateDaysBeforePeriodEnd: (value: number) => void;
}

export const SchedulingCard: React.FC<SchedulingCardProps> = ({
  documentType,
  enableRecurring,
  setEnableRecurring,
  recurringFrequency,
  setRecurringFrequency,
  recurringInterval,
  setRecurringInterval,
  recurringStartDate,
  setRecurringStartDate,
  recurringDayOfWeek,
  setRecurringDayOfWeek,
  recurringDayOfMonth,
  setRecurringDayOfMonth,
  recurringGenerateDaysBeforePeriodEnd,
  setRecurringGenerateDaysBeforePeriodEnd,
}) => {
  if (documentType !== 'invoice') return null;

  return (
    <Card variant="outlined" sx={{ mt: 3, borderColor: "primary.light", bgcolor: (t) => t.palette.action.hover }}>
      <CardHeader
        sx={{ pb: 1.5 }}
        title={
          <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
            <Box component="span" sx={{ color: "primary.main", display: "flex" }}><Calendar size={20} /></Box>
            Scheduling Options
          </Typography>
        }
        subheader="Optionally set up recurring billing for this invoice"
      />
      <CardContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Recurring Schedule Toggle */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box component="span" sx={{ color: "secondary.main", display: "flex" }}><Repeat size={16} /></Box>
                  <FormLabel sx={{ fontWeight: 500 }}>Recurring Invoice</FormLabel>
                </Box>
                <Typography component="p" sx={{ fontSize: 14, color: "text.secondary" }}>
                  Automatically create this invoice on a regular schedule
                </Typography>
              </Box>
              <Switch
                checked={enableRecurring}
                data-testid="recurring-toggle"
                onChange={(_e, checked) => {
                  setEnableRecurring(checked);
                  if (checked && !recurringStartDate) {
                    // Default to 1 month from now
                    const startDate = new Date();
                    startDate.setMonth(startDate.getMonth() + 1);
                    setRecurringStartDate(startDate.toISOString().split('T')[0]);
                  }
                }}
              />
            </Box>

            {enableRecurring && (
              <Box sx={{ pl: 3, borderLeft: 2, borderColor: "secondary.light", display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <FormLabel sx={{ display: "block", mb: 0.75 }}>Billing cadence</FormLabel>
                  <CadencePresetChips
                    frequency={recurringFrequency}
                    interval={recurringInterval}
                    onSelect={(freq, ivl) => {
                      setRecurringFrequency(freq);
                      setRecurringInterval(ivl);
                    }}
                  />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <Box>
                    <FormLabel>Frequency</FormLabel>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={recurringFrequency}
                      onChange={(e) => setRecurringFrequency(e.target.value as RecurringFrequency)}
                      sx={{ mt: 0.5 }}
                    >
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="quarterly">Quarterly</MenuItem>
                      <MenuItem value="yearly">Yearly</MenuItem>
                    </TextField>
                  </Box>
                  <Box>
                    <FormLabel>Every</FormLabel>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                      <TextField
                        type="number"
                        size="small"
                        inputProps={{ min: 1, max: 60 }}
                        value={recurringInterval}
                        onChange={(e) => setRecurringInterval(parseInt(e.target.value) || 1)}
                        sx={{ width: 80 }}
                      />
                      <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                        {cadenceUnitLabel(recurringFrequency)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {recurringFrequency === 'weekly' && (
                  <Box>
                    <FormLabel>Day of Week</FormLabel>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={recurringDayOfWeek.toString()}
                      onChange={(e) => setRecurringDayOfWeek(parseInt(e.target.value))}
                      sx={{ mt: 0.5 }}
                    >
                      <MenuItem value="0">Sunday</MenuItem>
                      <MenuItem value="1">Monday</MenuItem>
                      <MenuItem value="2">Tuesday</MenuItem>
                      <MenuItem value="3">Wednesday</MenuItem>
                      <MenuItem value="4">Thursday</MenuItem>
                      <MenuItem value="5">Friday</MenuItem>
                      <MenuItem value="6">Saturday</MenuItem>
                    </TextField>
                  </Box>
                )}

                {['monthly', 'quarterly', 'yearly'].includes(recurringFrequency) && (
                  <Box>
                    <FormLabel>Day of Month</FormLabel>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={recurringDayOfMonth.toString()}
                      onChange={(e) => setRecurringDayOfMonth(parseInt(e.target.value))}
                      sx={{ mt: 0.5 }}
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <MenuItem key={day} value={day.toString()}>
                          {day}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                )}

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <Box>
                    <FormLabel htmlFor="recurringStartDate">Start Date</FormLabel>
                    <TextField
                      id="recurringStartDate"
                      type="date"
                      size="small"
                      fullWidth
                      value={recurringStartDate}
                      onChange={(e) => setRecurringStartDate(e.target.value)}
                      inputProps={{ min: getLocalDateString() }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box>
                    <FormLabel htmlFor="recurringGenerateLead">Generate</FormLabel>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                      <WholeNumberField
                        id="recurringGenerateLead"
                        size="small"
                        data-testid="recurring-generate-lead"
                        inputProps={{ max: 31 }}
                        value={recurringGenerateDaysBeforePeriodEnd}
                        onChange={setRecurringGenerateDaysBeforePeriodEnd}
                        sx={{ width: 80 }}
                      />
                      <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                        day{recurringGenerateDaysBeforePeriodEnd === 1 ? '' : 's'} before the period ends
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, bgcolor: (t) => t.palette.action.selected, borderRadius: 1, fontSize: 14, color: "secondary.dark" }}>
                  <Box component="span" sx={{ flexShrink: 0, display: "flex" }}><Info size={16} /></Box>
                  <Box component="span">
                    Starting <strong>{recurringStartDate && new Date(recurringStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</strong>,
                    {' '}a new invoice covers each{' '}
                    <strong>{cadenceSummary(recurringFrequency, recurringInterval)}</strong>
                    {' '}period and is generated{' '}
                    <strong>
                      {recurringGenerateDaysBeforePeriodEnd === 1
                        ? 'on that period’s last day'
                        : `${recurringGenerateDaysBeforePeriodEnd} days before that period ends`}
                    </strong>
                    {' '}— after the service has been delivered, never before. A review copy is emailed to finance.
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
